import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import { cn } from "@/lib/utils"

const components: Components = {
	a: ({ node: _node, className, ...props }) => (
		<a
			className={cn(
				"font-medium text-foreground underline underline-offset-4",
				className,
			)}
			{...props}
		/>
	),
	blockquote: ({ node: _node, className, ...props }) => (
		<blockquote
			className={cn(
				"my-3 border-l-2 border-border pl-3 text-muted-foreground",
				className,
			)}
			{...props}
		/>
	),
	code: ({ node: _node, className, ...props }) => (
		<code
			className={cn(
				"rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]",
				className,
			)}
			{...props}
		/>
	),
	h1: ({ node: _node, className, ...props }) => (
		<h1
			className={cn("mb-2 mt-4 text-lg font-semibold", className)}
			{...props}
		/>
	),
	h2: ({ node: _node, className, ...props }) => (
		<h2
			className={cn("mb-2 mt-4 text-base font-semibold", className)}
			{...props}
		/>
	),
	h3: ({ node: _node, className, ...props }) => (
		<h3 className={cn("mb-1 mt-3 font-semibold", className)} {...props} />
	),
	h4: ({ node: _node, className, ...props }) => (
		<h4 className={cn("mb-1 mt-3 font-medium", className)} {...props} />
	),
	h5: ({ node: _node, className, ...props }) => (
		<h5 className={cn("mb-1 mt-3 font-medium", className)} {...props} />
	),
	h6: ({ node: _node, className, ...props }) => (
		<h6 className={cn("mb-1 mt-3 font-medium", className)} {...props} />
	),
	hr: ({ node: _node, className, ...props }) => (
		<hr className={cn("my-4 border-border", className)} {...props} />
	),
	img: ({ node: _node, className, ...props }) => (
		<img
			className={cn("my-3 h-auto max-w-full rounded-md", className)}
			{...props}
		/>
	),
	input: ({ node: _node, className, ...props }) => (
		<input
			className={cn("mr-1.5 align-middle accent-primary", className)}
			{...props}
		/>
	),
	li: ({ node: _node, className, ...props }) => (
		<li
			className={cn("my-1 pl-0.5 [&.task-list-item]:pl-0", className)}
			{...props}
		/>
	),
	ol: ({ node: _node, className, ...props }) => (
		<ol
			className={cn("my-2 list-decimal space-y-1 pl-5", className)}
			{...props}
		/>
	),
	p: ({ node: _node, className, ...props }) => (
		<p className={cn("my-2", className)} {...props} />
	),
	pre: ({ node: _node, className, ...props }) => (
		<pre
			className={cn(
				"my-3 overflow-x-auto rounded-md border bg-muted/60 p-3 font-mono text-xs [&>code]:bg-transparent [&>code]:p-0",
				className,
			)}
			{...props}
		/>
	),
	table: ({ node: _node, className, ...props }) => (
		<div className="my-3 overflow-x-auto">
			<table
				className={cn("w-full border-collapse text-left text-xs", className)}
				{...props}
			/>
		</div>
	),
	td: ({ node: _node, className, ...props }) => (
		<td className={cn("border px-2 py-1.5 align-top", className)} {...props} />
	),
	th: ({ node: _node, className, ...props }) => (
		<th
			className={cn("border bg-muted px-2 py-1.5 font-medium", className)}
			{...props}
		/>
	),
	ul: ({ node: _node, className, ...props }) => (
		<ul
			className={cn(
				"my-2 list-disc space-y-1 pl-5 [&.contains-task-list]:list-none",
				className,
			)}
			{...props}
		/>
	),
}

export const Markdown = ({
	children,
	className,
}: {
	children: string
	className?: string
}) => (
	<div
		className={cn(
			"min-w-0 break-words leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
			className,
		)}
	>
		<ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
			{children}
		</ReactMarkdown>
	</div>
)
